<template>
  <div id="pageTable">
    <!-- ===== Header info ===== -->
    <v-container grid-list-xl fluid>
      <v-card>
        <v-layout row wrap pa-2>
          <v-flex lg3 md3 sm12>
            <v-text-field
              label="Mã lệnh giao"
              :value="delivery.DeliveryCode || '(tự động)'"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-flex>
          <v-flex lg3 md3 sm12>
            <v-autocomplete
              label="Khách hàng"
              :items="customers"
              :item-text="(c) => c.Username + ' - ' + c.FullName"
              item-value="ID"
              v-model="delivery.CustomerID"
              :disabled="isEdit"
              outlined
              dense
              @change="onCustomerChange"
            ></v-autocomplete>
          </v-flex>
          <v-flex lg3 md3 sm12>
            <v-select
              label="Trạng thái"
              :items="[{ text: 'Chưa giao', value: 0 }, { text: 'Đã giao', value: 1 }]"
              item-text="text"
              item-value="value"
              v-model="delivery.Status"
              readonly
              outlined
              dense
            ></v-select>
          </v-flex>
          <v-flex lg3 md3 sm12>
            <v-text-field
              label="Ngày tạo"
              readonly
              :value="delivery.CreatedDate | display_date"
              outlined
              dense
            ></v-text-field>
          </v-flex>
        </v-layout>
      </v-card>
    </v-container>

    <div class="page-header-left pa-3 pt-0">
      <h4>Danh sách hàng hóa</h4>
    </div>

    <!-- ===== Product list ===== -->
    <v-container grid-list-xl fluid>
      <v-card>
        <v-layout row wrap pa-6>
          <v-flex lg12 md12 sm12>
            <v-layout row wrap justify-end pr-1>
              <v-btn
                class="mx-2"
                small
                color="red"
                @click="openProductDialog"
                :disabled="isEdit || !delivery.CustomerID"
                :loading="loadingProducts"
              ><v-icon dark>add</v-icon></v-btn>
            </v-layout>
          </v-flex>
          <v-flex lg12>
            <v-data-table
              :headers="productHeaders"
              :items="selectedProducts"
              disable-pagination
              hide-default-footer
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td><code>{{ item.OrderCode }}</code></td>
                  <td><code>{{ item.DhdCode }}</code></td>
                  <td>{{ item.ProductNameVN }}</td>
                  <td>{{ item.ProductNameEN }}</td>
                  <td><code>{{ item.HsCode }}</code></td>
                  <td>{{ item.Brand }}</td>
                  <td>{{ item.Model }}</td>
                  <td>{{ item.Unit }}</td>
                  <td class="text-right">{{ item.Quantity }}</td>
                  <td class="text-center">
                    <v-icon
                      medium
                      @click="removeProduct(item.ID)"
                      :disabled="isEdit"
                    >delete</v-icon>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isEdit"
            color="primary"
            :disabled="!delivery.CustomerID || selectedProducts.length === 0"
            :loading="saving"
            @click="saveDelivery"
          >
            <v-icon small class="mr-1">mdi-check-circle</v-icon> Tạo lệnh giao hàng
          </v-btn>
          <v-btn
            v-if="isEdit && delivery.Status === 0"
            color="success"
            :loading="saving"
            @click="markDelivered"
          >
            <v-icon small class="mr-1">mdi-truck-check</v-icon> Xác nhận đã giao hàng
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>

    <!-- ===== Product selection dialog ===== -->
    <v-dialog v-model="dialogProduct" max-width="1300px">
      <v-card>
        <v-card-title>
          <span class="title">Chọn hàng hóa cần giao</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1 white--text" @click="dialogProduct = false">Đóng</v-btn>
          <v-btn @click="confirmProductSelection">Lưu lại</v-btn>
        </v-card-actions>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout row wrap>
              <v-flex lg12>
                <v-text-field
                  v-model="productSearch"
                  label="Tìm kiếm (tên hàng, HS Code, mã đơn, DHD code...)"
                  prepend-inner-icon="search"
                  outlined
                  dense
                  clearable
                  class="mb-2"
                ></v-text-field>
                <v-data-table
                  v-model="dialogSelected"
                  fixed-header
                  height="450px"
                  :headers="popupHeaders"
                  :items="filteredAvailableProducts"
                  item-key="ID"
                  disable-pagination
                  hide-default-footer
                  show-select
                >
                  <template v-slot:item="{ item, isSelected, select }">
                    <tr :active="isSelected" @click="select(!isSelected)">
                      <td>
                        <v-checkbox primary hide-details :input-value="isSelected"></v-checkbox>
                      </td>
                      <td><code>{{ item.OrderCode }}</code></td>
                      <td><code>{{ item.DhdCode }}</code></td>
                      <td>{{ item.ProductNameVN }}</td>
                      <td>{{ item.ProductNameEN }}</td>
                      <td><code>{{ item.HsCode }}</code></td>
                      <td>{{ item.Brand }}</td>
                      <td>{{ item.Model }}</td>
                      <td>{{ item.Unit }}</td>
                      <td class="text-right">{{ item.Quantity }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApiService from '@/services/api.service'
import { sendSuccessNotice, sendErrorNotice } from '@/commons/utils'
import { mapState } from 'vuex'

export default {
  name: 'CnDeliveryDetail',

  data() {
    return {
      delivery: { ID: 0, CustomerID: null, Status: 0 },
      selectedProducts: [],
      customers: [],
      availableProducts: [],
      dialogProduct: false,
      dialogSelected: [],
      productSearch: '',
      saving: false,
      loadingProducts: false,

      productHeaders: [
        { text: 'Mã đơn hàng', value: 'OrderCode', sortable: false },
        { text: 'DHD Code', value: 'DhdCode', sortable: false },
        { text: 'Tên hàng VN / 越南品名', value: 'ProductNameVN', sortable: false },
        { text: 'Tên hàng EN / 英文品名', value: 'ProductNameEN', sortable: false },
        { text: 'HS Code', value: 'HsCode', sortable: false },
        { text: 'Nhãn hiệu', value: 'Brand', sortable: false },
        { text: 'Model', value: 'Model', sortable: false },
        { text: 'Đơn vị', value: 'Unit', sortable: false },
        { text: 'Số lượng', value: 'Quantity', align: 'right', sortable: false },
        { text: 'Thao tác', value: '', width: '80px', sortable: false },
      ],
      popupHeaders: [
        { text: 'Mã đơn hàng', value: 'OrderCode', sortable: false },
        { text: 'DHD Code', value: 'DhdCode', sortable: false },
        { text: 'Tên hàng VN', value: 'ProductNameVN', sortable: false },
        { text: 'Tên hàng EN', value: 'ProductNameEN', sortable: false },
        { text: 'HS Code', value: 'HsCode', sortable: false },
        { text: 'Nhãn hiệu', value: 'Brand', sortable: false },
        { text: 'Model', value: 'Model', sortable: false },
        { text: 'Đơn vị', value: 'Unit', sortable: false },
        { text: 'Số lượng', value: 'Quantity', align: 'right', sortable: false },
      ],
    }
  },

  computed: {
    deliveryId() {
      const pid = this.$route.params.Pid
      return pid && pid != '0' ? Number(pid) : 0
    },
    isEdit() {
      return !!(this.delivery.ID && this.delivery.ID !== 0)
    },
    filteredAvailableProducts() {
      if (!this.productSearch) return this.availableProducts
      const q = this.productSearch.toLowerCase()
      return this.availableProducts.filter(p =>
        (p.ProductNameVN || '').toLowerCase().includes(q) ||
        (p.ProductNameEN || '').toLowerCase().includes(q) ||
        (p.HsCode || '').toLowerCase().includes(q) ||
        (p.OrderCode || '').toLowerCase().includes(q) ||
        (p.DhdCode || '').toLowerCase().includes(q) ||
        (p.Brand || '').toLowerCase().includes(q) ||
        (p.Model || '').toLowerCase().includes(q)
      )
    }
  },

  created() {
    this.loadCustomers()
    if (this.deliveryId) {
      this.loadDelivery()
    }
  },

  mounted() {
    this.$root.$emit('REQUEST_ROOT_OPTION', {})
    this.$root.$on('REQUEST_REFRESH_DATA', this.refresh)
  },

  beforeDestroy() {
    this.$root.$off('REQUEST_REFRESH_DATA', this.refresh)
  },

  methods: {
    refresh() {
      if (this.deliveryId) this.loadDelivery()
    },

    async loadDelivery() {
      try {
        const res = await ApiService.get(`/api/cn-deliveries/${this.deliveryId}`)
        this.delivery = res.data.data || {}
        this.selectedProducts = this.delivery.products || []
      } catch (e) {
        sendErrorNotice('Không thể tải thông tin lệnh giao hàng.')
      }
    },

    async loadCustomers() {
      try {
        const res = await ApiService.query('/api/customers/', { rowsPerPage: -1 })
        this.customers = res.data.data || []
      } catch (e) {}
    },

    onCustomerChange() {
      this.selectedProducts = []
      this.availableProducts = []
    },

    async openProductDialog() {
      if (!this.delivery.CustomerID) {
        sendErrorNotice('Vui lòng chọn khách hàng trước.')
        return
      }
      this.loadingProducts = true
      try {
        const res = await ApiService.query('/api/cn-deliveries/available-products/', {
          customer_id: this.delivery.CustomerID
        })
        const alreadySelected = new Set(this.selectedProducts.map(p => p.ID))
        this.availableProducts = (res.data.data || []).filter(p => !alreadySelected.has(p.ID))
        this.dialogSelected = []
        this.productSearch = ''
        this.dialogProduct = true
      } catch (e) {
        sendErrorNotice('Không thể tải danh sách hàng hóa.')
      } finally {
        this.loadingProducts = false
      }
    },

    confirmProductSelection() {
      for (const p of this.dialogSelected) {
        if (!this.selectedProducts.find(sp => sp.ID === p.ID)) {
          this.selectedProducts.push(p)
        }
      }
      this.dialogSelected = []
      this.dialogProduct = false
    },

    removeProduct(productId) {
      this.selectedProducts = this.selectedProducts.filter(p => p.ID !== productId)
    },

    async saveDelivery() {
      if (!this.delivery.CustomerID) {
        sendErrorNotice('Vui lòng chọn khách hàng.')
        return
      }
      if (this.selectedProducts.length === 0) {
        sendErrorNotice('Vui lòng chọn ít nhất một hàng hóa.')
        return
      }
      this.saving = true
      try {
        const payload = {
          CustomerID: this.delivery.CustomerID,
          ProductIDs: this.selectedProducts.map(p => p.ID)
        }
        const res = await ApiService.put('/api/cn-deliveries/', payload)
        const newDelivery = res.data && res.data.data
        sendSuccessNotice('Tạo lệnh giao hàng thành công.')
        if (newDelivery && newDelivery.ID) {
          this.$router.replace({ name: 'CnDeliveryDetail', params: { Pid: newDelivery.ID } })
          this.delivery = newDelivery
          this.selectedProducts = newDelivery.products || []
        }
      } catch (e) {
        const reason = (e && e.body && typeof e.body === 'object' && e.body.reason)
          ? String(e.body.reason)
          : 'Không thể tạo lệnh giao hàng.'
        sendErrorNotice(reason)
      } finally {
        this.saving = false
      }
    },

    async markDelivered() {
      if (!confirm('Xác nhận giao hàng cho lệnh này?\nCác đơn hàng liên quan sẽ được chuyển sang trạng thái Đã giao hàng.')) return
      this.saving = true
      try {
        await ApiService.post(`/api/cn-deliveries/${this.deliveryId}/deliver/`, {})
        sendSuccessNotice('Giao hàng thành công.')
        this.loadDelivery()
      } catch (e) {
        const reason = (e && e.body && typeof e.body === 'object' && e.body.reason)
          ? String(e.body.reason)
          : 'Không thể xác nhận giao hàng.'
        sendErrorNotice(reason)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
